/**
 * The one source of truth for dropdown/context menu surfaces, across both menu families:
 *
 * - **Radix menus** (`ContextMenu`/`Menubar`) - `ProposalContextMenu`, `DeclinedProposalContextMenu`,
 *   `SearchVenueActionsMenu`, `VenueRowActionsMenu`. They highlight via `data-[highlighted]`, which
 *   Radix sets for pointer AND keyboard navigation alike.
 * - **Plain-button menus** - `KebabMenu` and the landing-page section menus, via
 *   `landing-page-v2/_components/_shared/menu-styles.ts`, which recomposes the fragments below.
 *   They highlight via `hover:`, because their keyboard nav moves DOM focus instead.
 *
 * That state-hook split is the only intended divergence: a `hover:` class would never fire under
 * Radix's arrow keys, and a `data-[highlighted]` class is inert on a plain `<button>`. Everything
 * else - row geometry, typography, the panel shell, the separator - is shared here so the two
 * families cannot drift again. They already did once: `CONTEXT_MENU_CONTENT_ANIMATION_CLS` named all
 * three of its consumers by name in a comment and they still ended up on four different radii.
 *
 * The row geometry deliberately matches `FetchToolbarSelect`'s options, so a menu and a toolbar
 * dropdown read as the same surface.
 *
 * Note the asymmetry in disabled rows: Radix keeps one element and flips `data-disabled`, so the
 * treatment is baked into the item strings below, while `KebabMenu` swaps the whole `className` and
 * therefore needs its own separate `DISABLED_MENU_ITEM_CLASS`. There is no Radix equivalent here.
 */

import { cn } from "@/lib/utils/index";
import { MENU_CONTENT_MOTION_CLASS } from "./constants";

/** Row state for a Radix menu item: resting colors plus the highlight/disabled data-attribute hooks. */
const RADIX_ITEM_STATE = cn(
  "cursor-pointer text-gray-700",
  "data-[highlighted]:bg-gray-100 data-[highlighted]:text-gray-900",
  "data-[disabled]:pointer-events-none data-[disabled]:text-gray-300"
);

/**
 * Shape, rhythm and typography of one menu row, with no horizontal padding and no state.
 * Horizontal padding is a separate token because the checkbox row indents to `pl-8` for its
 * absolutely-positioned indicator: `cn` is `twMerge`, which does NOT treat a later `pl-*` as
 * overriding an earlier `px-*`, so composing the two would leave correctness resting on the order
 * Tailwind happens to emit the rules in.
 */
export const MENU_ROW_BASE_CLASS =
  "relative flex select-none items-center gap-2 rounded-xl py-2 text-sm font-medium outline-none transition-colors";

/** Horizontal padding for a normal (non-indented) menu row. Pairs with {@link MENU_ROW_BASE_CLASS}. */
export const MENU_ROW_PX_CLASS = "px-3";

/** The panel itself: border, fill, radius and the inset that lets rows show their own rounding. */
export const MENU_PANEL_CLASS = "rounded-2xl border border-gray-200 bg-white p-1.5 shadow-lg";

/** The rule between two groups of items, inset so it doesn't run to the panel's padding edge. */
export const MENU_SEPARATOR_CLASS = "mx-1 my-1 h-px bg-gray-200";

/** A glyph pinned to a row's right edge: the chevron on a submenu trigger, or an external-link
 *  arrow on a row that opens a new tab. Always pair it with `aria-hidden`. */
export const MENU_ROW_TRAILING_ICON_CLASS = "ml-auto size-4 shrink-0 text-gray-400";

/** Radix `Content`/`SubContent` panel, including the open/close motion and stacking. */
export const RADIX_MENU_CONTENT_CLASS = cn("z-[100] min-w-[220px]", MENU_PANEL_CLASS, MENU_CONTENT_MOTION_CLASS);

/** Radix `Item`. Covers the disabled state too - see the note in this module's header. */
export const RADIX_MENU_ITEM_CLASS = cn(MENU_ROW_BASE_CLASS, MENU_ROW_PX_CLASS, RADIX_ITEM_STATE);

/** Radix `Item` for a destructive action (archive, decline, remove). */
export const RADIX_MENU_DANGER_ITEM_CLASS = cn(
  MENU_ROW_BASE_CLASS,
  MENU_ROW_PX_CLASS,
  "cursor-pointer text-red-600",
  "data-[highlighted]:bg-red-50 data-[highlighted]:text-red-700",
  "data-[disabled]:pointer-events-none data-[disabled]:text-red-300"
);

/** Radix `SubTrigger` - an item that also stays highlighted while its submenu is open. */
export const RADIX_MENU_SUB_TRIGGER_CLASS = cn(
  RADIX_MENU_ITEM_CLASS,
  "data-[state=open]:bg-gray-100 data-[state=open]:text-gray-900"
);

/** Radix `CheckboxItem` - indented to clear its absolutely-positioned `ItemIndicator`. */
export const RADIX_MENU_CHECKBOX_ITEM_CLASS = cn(MENU_ROW_BASE_CLASS, "pl-8 pr-3", RADIX_ITEM_STATE);

/** A non-interactive "nothing here yet" line standing in for a menu's items. */
export const RADIX_MENU_EMPTY_LABEL_CLASS = "px-3 py-2 text-sm font-medium text-gray-400";
